import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtQty } from "@/lib/format";

export const ACTIONS = { reserve: "Reservar", release: "Liberar reserva", prepare: "Registrar preparado", issue: "Registrar salida", consume: "Registrar consumo", return: "Registrar devolución", serial: "Asignar número de serie" };
const TRANSIT = "__transit";
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;

const locOptions = (action, line, stock, paths) => {
  if (action === "release") return Object.entries(line.reservations).map(([id, q]) => ({ id, label: `${paths[id] || id} · reservado por esta OS ${fmtQty(q)}` }));
  if (action === "consume" || action === "return") return Object.entries(line.outs).map(([id, q]) => ({ id, label: `${paths[id] || id} · en salida ${fmtQty(q)}` }));
  return stock.map((s) => ({ id: s.location_id, label: `${s.location_path} · disponible ${fmtQty(s.available)} · reservado ${fmtQty(s.reserved)}` }));
};

export const PrepActionDialog = ({ woId, line, action, onClose, onDone }) => {
  const [stock, setStock] = useState([]);
  const [locs, setLocs] = useState([]);
  const [f, setF] = useState({});
  const inv = action && action !== "prepare" && action !== "serial";
  useEffect(() => {
    if (!line || !action) return;
    setF({ quantity: "", location_id: null, destination: TRANSIT, serials: "", notes: "", serial: "" });
    if (inv) {
      api.get(`/inventory/items/${line.item_id}/stock`).then((r) => setStock(r.data.locations));
      api.get("/inventory/locations").then((r) => setLocs(r.data));
    }
  }, [line, action, inv]);
  if (!line || !action) return null;
  const paths = Object.fromEntries(locs.map((l) => [l.id, l.path]));
  const opts = locOptions(action, line, stock, paths);
  const withSerials = line.serialized && ["issue", "consume", "return"].includes(action);
  const submit = async () => {
    try {
      const url = `/work-orders/${woId}/preparation/lines/${line.id}`;
      const { data } = action === "serial" ? await api.post(`${url}/assign-serial`, { serial: f.serial })
        : await api.post(`${url}/action`, { action, quantity: Number(f.quantity), location_id: f.location_id, notes: f.notes,
          serials: withSerials ? f.serials.split(/[\n,]/).map((s) => s.trim()).filter(Boolean) : [],
          destination_location_id: action === "issue" && f.destination !== TRANSIT ? f.destination : null });
      toast.success(`${ACTIONS[action]}: listo`);
      onDone(data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="prep-action-dialog">
        <DialogHeader><DialogTitle className="font-display">{ACTIONS[action]} · {line.description}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Requerido {fmtQty(line.required)} · Reservado {fmtQty(line.reserved)} · Preparado {fmtQty(line.prepared)} · En salida {fmtQty(line.out_pending)} {line.unit}</p>
          {action === "serial" ? <F label="Número de serie"><Input value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} data-testid="prep-serial-input" /></F> : (
            <F label={`Cantidad (${line.unit})`}><Input type="number" step="any" min="0" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="prep-qty-input" /></F>
          )}
          {inv && <F label="Ubicación de inventario">
            <Select value={f.location_id || undefined} onValueChange={(v) => setF({ ...f, location_id: v })}>
              <SelectTrigger data-testid="prep-location-select"><SelectValue placeholder={opts.length ? "Seleccionar" : "Sin existencias registradas"} /></SelectTrigger>
              <SelectContent>{opts.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          {action === "issue" && <F label="Destino / custodio">
            <Select value={f.destination} onValueChange={(v) => setF({ ...f, destination: v })}>
              <SelectTrigger data-testid="prep-destination-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={TRANSIT}>En tránsito / pendiente de entrega (OS)</SelectItem>{locs.filter((l) => l.active && l.id !== f.location_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.path}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          {withSerials && <F label="Números de serie (uno por línea, opcional; los no capturados salen como 'Sin serial')"><Textarea rows={3} value={f.serials} onChange={(e) => setF({ ...f, serials: e.target.value })} data-testid="prep-serials-input" /></F>}
          {action !== "serial" && <F label="Notas"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="prep-notes-input" /></F>}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} data-testid="prep-action-submit">Confirmar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
